import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function AuthModal() {
  const { authModal, closeAuth, setAuthTab, login, register } = useAuth();
  const [tab, setTab] = useState(authModal.tab);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTab(authModal.tab);
    setError("");
  }, [authModal]);

  const switchTab = (t) => {
    setTab(t);
    setAuthTab(t);
    setError("");
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (tab === "login") {
        await login(email, password);
        toast.success("Welcome back.");
      } else {
        await register(name, email, password);
        toast.success("Account created — welcome to the club.");
      }
      closeAuth();
      setName(""); setEmail(""); setPassword("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authModal.open} onOpenChange={(o) => !o && closeAuth()}>
      <DialogContent data-testid="auth-modal-container" className="bg-surface border-white/10 text-porcelain sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">
            {tab === "login" ? "Welcome back" : "Join the club"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-1 bg-obsidian p-1 rounded-full border border-white/10">
          <button data-testid="auth-login-tab-button" onClick={() => switchTab("login")}
            className={`text-xs font-mono uppercase tracking-[0.15em] py-2.5 rounded-full transition-colors duration-300 ${tab === "login" ? "bg-kinetic text-obsidian font-medium" : "text-ash hover:text-porcelain"}`}>
            Sign In
          </button>
          <button data-testid="auth-register-tab-button" onClick={() => switchTab("register")}
            className={`text-xs font-mono uppercase tracking-[0.15em] py-2.5 rounded-full transition-colors duration-300 ${tab === "register" ? "bg-kinetic text-obsidian font-medium" : "text-ash hover:text-porcelain"}`}>
            Register
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3 mt-2">
          {tab === "register" && (
            <input data-testid="auth-name-input" type="text" required minLength={2} value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Your name"
              className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors duration-300" />
          )}
          <input data-testid="auth-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="Email"
            className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors duration-300" />
          <input data-testid="auth-password-input" type="password" required minLength={6} value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 characters)"
            className="bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3.5 text-sm transition-colors duration-300" />
          {error && <p data-testid="auth-error-message" className="text-crimson text-xs font-mono">{error}</p>}
          <button data-testid="auth-submit-form-button" type="submit" disabled={busy}
            className="bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium py-4 rounded-full hover:bg-porcelain transition-colors duration-300 disabled:opacity-50 mt-1">
            {busy ? "Hold on…" : tab === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-1">
          <span className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-ash">or</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <button data-testid="auth-google-button" type="button" onClick={loginWithGoogle}
          className="w-full border border-white/15 hover:border-kinetic/60 text-porcelain font-mono text-xs uppercase tracking-[0.18em] py-4 rounded-full flex items-center justify-center gap-2.5 transition-colors duration-300">
          <svg width="15" height="15" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>

        <p className="text-ash text-xs leading-relaxed mt-1">
          Members can publish opinions, join debates and comment on every story. Editors get the Studio.
        </p>
      </DialogContent>
    </Dialog>
  );
}
